import React, { useEffect, useState } from 'react';
import { useAppStore, useWallet, useTransactions } from '../stores/useAppStore';
import { useSetCurrentPage } from '../stores/useAppStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import PageHeader from '../components/PageHeader';
import { useToastStore } from '../stores/useToastStore';
import GlassCard from '../components/ui/GlassCard';
import ConfirmDialog from '../components/ConfirmDialog';
import { decrypt } from '../utils/cryptoUtils';
import { copyToClipboard } from '../utils/clipboardUtils';

const Wallet: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const wallet = useWallet();
  const wallets = useAppStore((state) => state.wallets);
  const transactions = useTransactions();
  const fetchWallet = useAppStore((state) => state.fetchWallet);
  const fetchWallets = useAppStore((state) => state.fetchWallets);
  const setWallet = useAppStore((state) => state.setWallet);
  const isFetchingWallet = useAppStore((state) => state.isFetchingWallet);
  const isFetchingTransactions = useAppStore((state) => state.isFetchingTransactions);
  const fetchTransactions = useAppStore((state) => state.fetchTransactions);
  const createWallet = useAppStore((state) => state.createWallet);
  const deleteWallet = useAppStore((state) => state.deleteWallet);
  const showToast = useToastStore((state) => state.show);

  const [selectedChain, setSelectedChain] = useState<'POLYGON' | 'TRC20' | 'SOLANA'>('POLYGON');
  const [isCreating, setIsCreating] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);
  const [showWalletList, setShowWalletList] = useState(false);
  const [decryptedMnemonic, setDecryptedMnemonic] = useState('');
  const [decryptedPrivateKey, setDecryptedPrivateKey] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [walletToDelete, setWalletToDelete] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleShowSecrets = () => {
    if (wallet?.mnemonic && wallet?.privateKey) {
      // 检查是否已经是解密后的状态 (以太坊助记词通常以单词开头，私钥以 0x 开头)
      // 如果看起来像加密后的 (通常是 Base64 格式)，则进行解密
      let mnemonic = wallet.mnemonic;
      let privateKey = wallet.privateKey;

      try {
        // 如果是加密的，解密后应该包含空格 (助记词) 或 0x (私钥)
        // 这里的逻辑可以根据加密工具的具体实现来微调
        const decryptedM = decrypt(mnemonic);
        const decryptedP = decrypt(privateKey);

        if (decryptedM) mnemonic = decryptedM;
        if (decryptedP) privateKey = decryptedP;
      } catch (e) {
        console.log('Already decrypted or decryption failed');
      }

      setDecryptedMnemonic(mnemonic);
      setDecryptedPrivateKey(privateKey);
      setShowSecrets(true);
    } else {
      showToast('该钱包未保存备份信息', 'info');
    }
  };

  useEffect(() => {
    fetchWallets();
    fetchTransactions();
  }, [fetchWallets, fetchTransactions]);

  // 当切换链时，尝试从已加载的钱包列表中寻找匹配的钱包
  useEffect(() => {
    if (wallets.length > 0) {
      const found = wallets.find((w) => w.chain === selectedChain);
      if (found) {
        setWallet(found);
      } else {
        // 如果没找到，且不是正在加载中，则尝试获取或提示创建
        fetchWallet('USDT', selectedChain);
      }
    } else {
      fetchWallet('USDT', selectedChain);
    }
  }, [selectedChain, wallets, fetchWallet, setWallet]);

  const handleCreateWallet = async () => {
    try {
      setIsCreating(true);
      await createWallet('USDT', selectedChain);
      await fetchWallets(); // 重新加载列表
      showToast(`${selectedChain} 钱包创建成功`, 'success');
    } catch (error: any) {
      showToast(error.message || '创建钱包失败', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleSwitchWallet = (newWallet: any) => {
    setWallet(newWallet);
    setSelectedChain(newWallet.chain as any);
    setShowWalletList(false);
    if (newWallet.id) {
      fetchTransactions({ walletId: newWallet.id.toString() });
    }
  };

  const handleDeleteWalletClick = (e: React.MouseEvent, id: number) => {
    e.stopPropagation(); // 阻止触发切换钱包
    setWalletToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteWallet = async () => {
    if (!walletToDelete) return;

    try {
      setIsDeleting(true);
      await deleteWallet(walletToDelete);
      showToast('钱包删除成功', 'success');
      setShowDeleteConfirm(false);
      setWalletToDelete(null);
    } catch (error: any) {
      showToast(error.message || '删除钱包失败', 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <GradientBackground>
      <div className="min-h-screen">
        <ParticleBackground />
        <PageHeader title="我的钱包" onBack={() => setCurrentPage('personal-center')} />

        {/* 内容区域 */}
        <div className="px-4 py-4 pt-20 max-w-lg mx-auto">
          {/* 链切换与钱包列表 */}
          <div className="flex gap-2 mb-6">
            <GlassCard className="flex-1 flex p-1 !rounded-2xl overflow-hidden border-white/10" hover={false}>
              {(['POLYGON', 'TRC20', 'SOLANA'] as const).map((chain) => (
                <button
                  key={chain}
                  onClick={() => setSelectedChain(chain)}
                  className={`flex-1 py-2.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all duration-500 ${
                    selectedChain === chain
                      ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-500/30'
                      : 'text-white/40 hover:text-white/70'
                  }`}
                >
                  {chain === 'POLYGON' ? 'Polygon' : chain === 'TRC20' ? 'TRC20' : 'Solana'}
                </button>
              ))}
            </GlassCard>

            <button
              onClick={() => setShowWalletList(true)}
              className="px-4 bg-white/5 border border-white/10 rounded-2xl text-white/70 hover:bg-white/10 transition-all flex items-center justify-center gap-2"
            >
              <span className="text-lg">👛</span>
              <span className="text-[10px] font-black uppercase tracking-widest hidden sm:inline">我的钱包</span>
            </button>
          </div>

          {/* 资产概览卡片 */}
          <GlassCard className="p-6 mb-6 overflow-hidden group relative" glow={true}>
            {/* 动态背景装饰 */}
            <div className="absolute top-0 right-0 -mr-16 -mt-16 w-48 h-48 bg-indigo-500/20 rounded-full blur-[80px] group-hover:bg-indigo-500/30 transition-all duration-700 animate-pulse"></div>
            <div className="absolute bottom-0 left-0 -ml-16 -mb-16 w-48 h-48 bg-purple-500/20 rounded-full blur-[80px] group-hover:bg-purple-500/30 transition-all duration-700"></div>

            <div className="relative z-10">
              <div className="flex justify-between items-start mb-8">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-white/70 text-[10px] font-black uppercase tracking-[0.2em]">总资产估值</span>
                    <span className="flex h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-bold text-white tracking-tighter">
                      {typeof wallet?.balance === 'number' ? wallet.balance.toFixed(2) : '0.00'}
                    </span>
                    <span className="text-sm font-bold text-white/40 uppercase tracking-widest">USDT</span>

                    {wallet && (
                      <button
                        onClick={() => {
                          fetchWallet('USDT', selectedChain, true);
                          if (wallet?.id) {
                            fetchTransactions({ walletId: wallet.id.toString(), sync: true });
                          }
                        }}
                        disabled={isFetchingWallet || isFetchingTransactions}
                        className={`ml-3 p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 transition-all duration-500 ${
                          isFetchingWallet || isFetchingTransactions
                            ? 'animate-spin opacity-50'
                            : 'hover:scale-110 active:scale-90'
                        }`}
                      >
                        <span className="text-sm">🔄</span>
                      </button>
                    )}
                  </div>
                </div>

                <div className="px-3 py-1.5 rounded-lg bg-indigo-500/10 border border-indigo-500/20 backdrop-blur-md">
                  <div className="text-[8px] text-indigo-400 font-black uppercase tracking-widest text-center mb-0.5">
                    Network
                  </div>
                  <div className="text-[10px] text-white font-black tracking-widest">{selectedChain}</div>
                </div>
              </div>

              {wallet ? (
                <div className="space-y-4">
                  {/* 收款地址卡片 */}
                  <div
                    className="bg-black/30 rounded-2xl p-4 border border-white/5 group/addr cursor-pointer hover:bg-black/40 transition-all duration-300"
                    onClick={async () => {
                      const success = await copyToClipboard(wallet.address);
                      if (success) showToast('地址已复制', 'success');
                    }}
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white/30 text-[9px] font-black uppercase tracking-widest">
                        我的收款地址
                      </span>
                      <span className="text-indigo-400 text-[9px] font-black uppercase tracking-widest transition-all flex items-center gap-1.5">
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2"
                          />
                        </svg>
                        点击复制
                      </span>
                    </div>
                    <div className="text-white/80 font-mono text-xs break-all leading-relaxed tracking-wider">
                      {wallet.address}
                    </div>
                  </div>

                  {/* 安全中心入口 */}
                  <button
                    onClick={handleShowSecrets}
                    className="w-full py-3.5 bg-gradient-to-r from-white/5 to-white/[0.02] hover:from-white/10 hover:to-white/5 border border-white/10 rounded-2xl text-white/60 text-[11px] font-black uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-3 group"
                  >
                    <span className="text-lg group-hover:rotate-12 transition-transform">🔐</span>
                    安全备份与私钥管理
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleCreateWallet}
                  disabled={isCreating}
                  className="w-full py-5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl font-black uppercase tracking-[0.2em] text-sm shadow-xl shadow-indigo-600/20 hover:shadow-indigo-600/40 hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-500 disabled:opacity-50"
                >
                  {isCreating ? '正在极速创建...' : `立即创建 ${selectedChain} 钱包`}
                </button>
              )}
            </div>
          </GlassCard>

          {/* 核心操作面板 */}
          {wallet && (
            <div className="grid grid-cols-2 gap-4 mb-8">
              <GlassCard
                onClick={() => setCurrentPage('deposit')}
                className="flex flex-col items-center justify-center gap-3 py-6 group"
              >
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-2xl group-hover:scale-110 group-hover:bg-emerald-500/20 transition-all duration-500 shadow-lg shadow-emerald-500/10">
                  <span className="group-hover:translate-y-0.5 transition-transform">⬇️</span>
                </div>
                <span className="text-xs font-black text-white/80 uppercase tracking-[0.2em]">充币充值</span>
              </GlassCard>

              <GlassCard
                onClick={() => setCurrentPage('withdraw')}
                className="flex flex-col items-center justify-center gap-3 py-6 group"
              >
                <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 flex items-center justify-center text-2xl group-hover:scale-110 group-hover:bg-indigo-500/20 transition-all duration-500 shadow-lg shadow-indigo-500/10">
                  <span className="group-hover:-translate-y-0.5 transition-transform">⬆️</span>
                </div>
                <span className="text-xs font-black text-white/80 uppercase tracking-[0.2em]">提币提现</span>
              </GlassCard>
            </div>
          )}

          {/* 交易历史记录 */}
          <div className="flex items-center justify-between mb-5 px-1">
            <h2 className="text-white text-sm font-black uppercase tracking-[0.3em]">交易历史</h2>
            <div className="h-px w-24 bg-gradient-to-r from-white/20 to-transparent"></div>
          </div>

          <div className="space-y-3 pb-24">
            {transactions.length > 0 ? (
              transactions.map((tx: any) => (
                <GlassCard key={tx.id} className="p-4 flex items-center justify-between !bg-white/[0.03]">
                  <div className="flex items-center gap-4">
                    <div
                      className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl shadow-inner ${
                        tx.type === 'deposit'
                          ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                          : 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'
                      }`}
                    >
                      {tx.type === 'deposit' ? '↓' : '↑'}
                    </div>
                    <div>
                      <div className="text-white text-xs font-black tracking-wider mb-1">
                        {tx.type === 'deposit' ? '账户充值' : '账户提现'}
                      </div>
                      <div className="text-white/30 text-[9px] font-bold uppercase tracking-widest">
                        {new Date(tx.createdAt).toLocaleDateString()} ·{' '}
                        {new Date(tx.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div
                      className={`text-sm font-black tracking-tighter ${tx.type === 'deposit' ? 'text-emerald-400' : 'text-indigo-400'}`}
                    >
                      {tx.type === 'deposit' ? '+' : '-'}
                      {typeof tx.amount === 'number' ? tx.amount.toFixed(2) : '0.00'}
                    </div>
                    <div className="text-white/20 text-[9px] font-black uppercase tracking-widest">{tx.currency}</div>
                  </div>
                </GlassCard>
              ))
            ) : (
              <GlassCard className="py-16 text-center" hover={false}>
                <div className="text-3xl mb-4 opacity-20">📂</div>
                <div className="text-white/50 text-[10px] font-black uppercase tracking-[0.3em]">暂无任何交易记录</div>
              </GlassCard>
            )}
          </div>
        </div>
      </div>

      {/* 敏感信息弹窗 */}
      {showSecrets && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowSecrets(false)}></div>
          <div className="relative bg-zinc-900 border border-white/10 rounded-3xl w-full max-w-sm p-6 shadow-2xl overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 to-purple-500"></div>

            <div className="flex justify-between items-center mb-6">
              <h3 className="text-white font-bold text-lg">钱包备份信息</h3>
              <button onClick={() => setShowSecrets(false)} className="text-white/40 hover:text-white">
                ✕
              </button>
            </div>

            <div className="space-y-6">
              <div className="space-y-2">
                <label className="text-white/40 text-xs uppercase tracking-wider">助记词 (Mnemonic)</label>
                <div className="bg-black/40 border border-white/5 rounded-2xl p-4 text-white/90 text-sm font-medium leading-relaxed break-words">
                  {decryptedMnemonic}
                </div>
                <button
                  onClick={async () => {
                    const success = await copyToClipboard(decryptedMnemonic);
                    if (success) showToast('助记词已复制', 'success');
                  }}
                  className="text-indigo-400 text-xs font-bold px-1"
                >
                  复制助记词
                </button>
              </div>

              <div className="space-y-2">
                <label className="text-white/40 text-xs uppercase tracking-wider">私钥 (Private Key)</label>
                <div className="bg-black/40 border border-white/5 rounded-2xl p-4 text-white/90 text-sm font-mono break-all leading-relaxed">
                  {decryptedPrivateKey}
                </div>
                <button
                  onClick={async () => {
                    const success = await copyToClipboard(decryptedPrivateKey);
                    if (success) showToast('私钥已复制', 'success');
                  }}
                  className="text-indigo-400 text-xs font-bold px-1"
                >
                  复制私钥
                </button>
              </div>

              <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-3 flex gap-3 items-start">
                <span className="text-lg">⚠️</span>
                <p className="text-orange-200/60 text-[10px] leading-normal">
                  警告：请妥善保管您的助记词和私钥。任何拥有这些信息的人都可以完全控制您的资产。请勿将其透露给任何人。
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowSecrets(false)}
              className="w-full mt-8 py-4 bg-white text-black rounded-2xl font-bold active:scale-95 transition-all"
            >
              我知道了
            </button>
          </div>
        </div>
      )}

      {/* 钱包列表切换弹窗 */}
      {showWalletList && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center px-0 sm:px-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowWalletList(false)}></div>
          <div className="relative bg-zinc-900 border-t sm:border border-white/10 rounded-t-[2.5rem] sm:rounded-3xl w-full max-w-sm p-6 shadow-2xl overflow-hidden max-h-[80vh] flex flex-col">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 to-purple-500"></div>

            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-white font-bold text-lg">切换钱包</h3>
                <p className="text-white/40 text-[10px] uppercase tracking-widest mt-0.5">我的所有资产账户</p>
              </div>
              <button
                onClick={() => setShowWalletList(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 text-white/40 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto pr-1 space-y-3 custom-scrollbar">
              {wallets.length > 0 ? (
                wallets.map((w: any) => (
                  <button
                    key={w.id}
                    onClick={() => handleSwitchWallet(w)}
                    className={`w-full p-4 rounded-2xl border transition-all flex items-center justify-between group ${
                      wallet?.id === w.id
                        ? 'bg-indigo-600/10 border-indigo-500/50'
                        : 'bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/20'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${
                          w.chain === 'POLYGON' ? 'bg-purple-500/10 text-purple-400' : 'bg-blue-500/10 text-blue-400'
                        }`}
                      >
                        {w.chain === 'POLYGON' ? 'P' : 'T'}
                      </div>
                      <div className="text-left">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-black text-xs tracking-wider">{w.currency} Wallet</span>
                          {wallet?.id === w.id && (
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                          )}
                        </div>
                        <div className="text-white/30 text-[9px] font-mono break-all line-clamp-1 max-w-[140px]">
                          {w.address}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right mr-2">
                        <div className="text-white font-bold text-xs">{Number(w.balance).toFixed(2)}</div>
                        <div className="text-white/20 text-[8px] font-black uppercase tracking-widest">{w.chain}</div>
                      </div>
                      <button
                        onClick={(e) => handleDeleteWalletClick(e, w.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-xl bg-red-500/10 text-red-400 hover:bg-red-500/20 active:scale-90 transition-all border border-red-500/10"
                        title="删除钱包"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          stroke-width="2.5"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        >
                          <path d="M3 6h18"></path>
                          <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                          <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </button>
                ))
              ) : (
                <div className="py-12 text-center text-white/20">
                  <div className="text-3xl mb-4">📭</div>
                  <div className="text-[10px] font-black uppercase tracking-[0.3em]">暂无钱包</div>
                </div>
              )}
            </div>

            <button
              onClick={() => {
                setShowWalletList(false);
                // 这里可以跳转到创建钱包逻辑，或者直接关闭
              }}
              className="w-full mt-6 py-4 bg-white/5 hover:bg-white/10 text-white/60 border border-white/10 rounded-2xl font-bold text-xs uppercase tracking-widest transition-all"
            >
              关闭
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        show={showDeleteConfirm}
        title="确认删除钱包"
        message="确定要删除该钱包吗？此操作将永久移除该钱包及其所有交易记录，且不可撤销。"
        confirmText="确认删除"
        cancelText="取消"
        loading={isDeleting}
        onConfirm={confirmDeleteWallet}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setWalletToDelete(null);
        }}
      />
    </GradientBackground>
  );
};

export default Wallet;
